import { SUPPORT_HOLIDAY_NOTICE } from '../constants'

interface HolidayWindow {
  /** Inclusive first day the notice shows, `YYYY-MM-DD`. */
  startsOn: string
  /** Inclusive last day the notice shows, `YYYY-MM-DD`. */
  endsOn: string
}

/**
 * Whether the summer-holiday support notice should be shown right now.
 *
 * The window is compared on calendar-day boundaries in the viewer's local
 * time: the notice appears from the start of `startsOn` through the end of
 * `endsOn` (both inclusive), and is hidden otherwise. A malformed or inverted
 * window is treated as "never show" rather than throwing, so a copy edit can
 * never take the app down.
 */
export function isSupportHolidayNoticeActive(
  now: Date = new Date(),
  window: HolidayWindow = SUPPORT_HOLIDAY_NOTICE
): boolean {
  const start = startOfDay(window.startsOn)
  const end = endOfDay(window.endsOn)
  if (start === null || end === null || start > end) {
    return false
  }
  const t = now.getTime()
  return t >= start && t <= end
}

/** Parse a `YYYY-MM-DD` date at 00:00:00.000 local time, or null if invalid. */
function startOfDay(isoDate: string): number | null {
  const parts = parseIsoDate(isoDate)
  if (parts === null) {
    return null
  }
  const [year, month, day] = parts
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime()
}

/** Parse a `YYYY-MM-DD` date at 23:59:59.999 local time, or null if invalid. */
function endOfDay(isoDate: string): number | null {
  const parts = parseIsoDate(isoDate)
  if (parts === null) {
    return null
  }
  const [year, month, day] = parts
  return new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
}

function parseIsoDate(isoDate: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match) {
    return null
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  // Reject impossible months/days (and calendar overflow like 2026-02-30,
  // which the Date constructor would otherwise silently roll forward).
  const candidate = new Date(year, month - 1, day)
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null
  }
  return [year, month, day]
}
